import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth';
import { authApi } from '@/api/modules/auth';
import { ROUTE_LOGIN } from '@/constants/routes';

/**
 * 路由守卫。
 *
 * 1) 无 token：跳 /login（state 带 from 用于回跳）
 * 2) 有 token 但 user 为 null（刷新页面后只持久化了 token）：
 *    自动调 /users/me 拉回用户对象。me 失败（401）则 logout
 * 3) 上述完成后才渲染子节点
 */
export function RouteGuard({ children }: { children: ReactNode }) {
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setUser = useAuthStore((s) => s.setUser);
  const logout = useAuthStore((s) => s.logout);
  const location = useLocation();

  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => authApi.me(),
    enabled: !!token && !user,
    retry: false,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (meQuery.data) setUser(meQuery.data);
  }, [meQuery.data, setUser]);

  useEffect(() => {
    if (meQuery.isError) logout();
  }, [meQuery.isError, logout]);

  if (!token || !isAuthenticated) {
    return <Navigate to={ROUTE_LOGIN} replace state={{ from: location }} />;
  }

  if (!user) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          background: 'linear-gradient(135deg, #f0f5ff 0%, #ffffff 60%, #e6f4ff 100%)',
          zIndex: 1000,
        }}
      >
        <Spin size="large" />
        <div style={{ color: '#1677ff', fontSize: 14, letterSpacing: 1 }}>加载中…</div>
      </div>
    );
  }

  return <>{children}</>;
}
